import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api, apiErr } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { Plus, Gauge, ArrowDown, ArrowUp, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY = { structure_id: "", name: "", download_mbps: 20, upload_mbps: 5, session_timeout_min: 1440, idle_timeout_min: 30, traffic_limit_mb: 0, max_devices: 2 };

export default function Profiles() {
  const { t } = useI18n();
  const { effectiveStructure } = useAuth();
  const [items, setItems] = useState([]);
  const [structures, setStructures] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => {
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/profiles${p}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); api.get("/structures").then((r) => setStructures(r.data)); }, [effectiveStructure]);

  const save = async () => {
    try {
      const body = { ...form };
      ["download_mbps", "upload_mbps", "session_timeout_min", "idle_timeout_min", "traffic_limit_mb", "max_devices"].forEach((k) => (body[k] = Number(body[k])));
      if (editId) await api.put(`/profiles/${editId}`, body); else await api.post("/profiles", body);
      toast.success("✓"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (p) => { await api.delete(`/profiles/${p.id}`); toast.success("✓"); load(); };

  const NUM = [["download_mbps", t("prof.download")], ["upload_mbps", t("prof.upload")], ["session_timeout_min", t("prof.session_timeout")], ["idle_timeout_min", t("prof.idle_timeout")], ["traffic_limit_mb", t("prof.traffic_limit")], ["max_devices", t("prof.max_devices")]];

  return (
    <div data-testid="profiles-page">
      <PageHeader title={t("prof.title")} action={
        <button data-testid="new-profile-btn" onClick={() => { setForm({ ...EMPTY, structure_id: effectiveStructure || structures[0]?.id || "" }); setEditId(null); setOpen(true); }}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2.5 text-sm font-semibold transition-colors">
          <Plus className="h-4 w-4" /> {t("prof.new")}
        </button>} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} data-testid={`profile-card-${p.id}`} className="rounded-xl border border-[#1F2937] bg-[#111827] p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-blue-500/10 ring-1 ring-blue-500/20 flex items-center justify-center"><Gauge className="h-4.5 w-4.5 text-blue-400" /></div>
                <h3 className="font-display font-semibold">{p.name}</h3>
              </div>
              <div className="flex gap-1">
                <button data-testid={`edit-profile-${p.id}`} onClick={() => { setForm({ ...p }); setEditId(p.id); setOpen(true); }} className="p-1.5 rounded-md hover:bg-white/5 text-slate-400"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`delete-profile-${p.id}`} onClick={() => del(p)} className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-4 flex gap-4">
              <div className="flex items-center gap-1.5"><ArrowDown className="h-4 w-4 text-emerald-400" /><span className="font-mono text-lg font-bold">{p.download_mbps}</span><span className="text-xs text-slate-500">Mbps</span></div>
              <div className="flex items-center gap-1.5"><ArrowUp className="h-4 w-4 text-cyan-400" /><span className="font-mono text-lg font-bold">{p.upload_mbps}</span><span className="text-xs text-slate-500">Mbps</span></div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-400">
              <div>Session: {p.session_timeout_min || "∞"} min</div>
              <div>Idle: {p.idle_timeout_min || "∞"} min</div>
              <div>{t("prof.max_devices")}: {p.max_devices}</div>
              <div>{t("prof.traffic_limit")}: {p.traffic_limit_mb || "∞"}</div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? t("common.edit") : t("prof.new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.name")}</label>
              <input data-testid="profile-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("nav.structures")}</label>
              <select data-testid="profile-structure-select" value={form.structure_id} onChange={(e) => setForm({ ...form, structure_id: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm">
                {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {NUM.map(([k, label]) => (
              <div key={k}>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
                <input data-testid={`profile-input-${k}`} type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-slate-400">{t("common.cancel")}</button>
            <button data-testid="save-profile-btn" onClick={save} className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-semibold">{t("common.save")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
